import { Button } from "@clockwise/design-system";
import { Warning } from "@clockwise/design-system/icons";
import { border_subtle } from "@clockwise/design-system/tokens";
import { LockFilled, SvgIconComponent } from "@clockwise/icons";
import { RecurrenceIntervalTypeEnum } from "@clockwise/schema";
import { ToggleOffFlex } from "@clockwise/web-commons/src/components/svgs/ToggleOffFlex";
import { ToggleOnFlex } from "@clockwise/web-commons/src/components/svgs/ToggleOnFlex";
import { formatDuration } from "@clockwise/web-commons/src/util/duration.util";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { Tooltip } from "@material-ui/core";
import classNames from "classnames";
import { isEmpty, noop } from "lodash";
import { Interval } from "luxon";
import React from "react";
import { useChangeFlexToggle } from "../chat/hooks/useChangeFlexToggle";
import { formattedDateTime } from "../web-app-calendar/calendar-popover/utils/formattedDateTime";
import { formatRecurrenceToSelectableOption } from "../web-app-calendar/hooks/useEditExistingEvent.util";
import { ECAttendeeStack } from "./atoms/ECAttendeeStack";
import { EventCardAttendee } from "./types";

type RecurrenceEventInfo = {
  intervalCount: number;
  intervalType: RecurrenceIntervalTypeEnum;
  recurrenceString: string;
};

const Divider = () => {
  return <div className={`cw-h-2 cw-w-[1px] cw-mx-1.5 cw-bg-[${border_subtle}]`} />;
};

const EventInfoNewEvent = ({
  attendeePeople,
  interval,
  recurrence,
  isFlexible,
}: {
  attendeePeople?: EventCardAttendee[];
  interval?: Interval;
  recurrence?: RecurrenceEventInfo;
  isFlexible?: boolean;
}) => {
  const [onChangeFlexToggle] = useChangeFlexToggle({
    onCompleted: noop,
    onError: noop,
  });
  const startTimeFormatted =
    interval?.start && formattedDateTime(interval.start, getRenderTimeZone());
  const hasAttendees = attendeePeople && !isEmpty(attendeePeople);
  const flexibilityFetched = isFlexible !== undefined;

  const isRecurring =
    recurrence &&
    recurrence.intervalType !== RecurrenceIntervalTypeEnum.NONE &&
    recurrence.intervalCount !== 0;

  const recurrenceInterval = isRecurring
    ? formatRecurrenceToSelectableOption({ ...recurrence, supportCustomRecurrence: true })
    : null;
  const recurrenceString = recurrence?.recurrenceString ?? null;

  return (
    <div className="cw-flex cw-items-center cw-caption cw-text-muted">
      {flexibilityFetched && (
        <div className="cw-mt-1">
          <Button
            size="mini"
            sentiment="neutral"
            variant="outlined"
            onClick={() => {
              void onChangeFlexToggle(!isFlexible);
            }}
            startIcon={isFlexible ? ToggleOnFlex : ToggleOffFlex}
          >
            Flexible
          </Button>
        </div>
      )}
      {flexibilityFetched && <Divider />}
      {hasAttendees && (
        <>
          <ECAttendeeStack attendees={attendeePeople} maxShown={3} />
          <Divider />
        </>
      )}
      {startTimeFormatted && startTimeFormatted}
      {recurrenceInterval && (
        <>
          {startTimeFormatted && <span className="cw-mx-1"> ⸱ </span>}
          <Tooltip title={<div>{recurrenceString ?? `Recurs ${recurrenceInterval}`}</div>}>
            <span>{recurrenceInterval}</span>
          </Tooltip>
        </>
      )}
    </div>
  );
};

const EventInfoDefault = ({
  attendeePeople,
  interval,
  recurrence,
  subText,
  subTextIcon: SubTextIcon,
}: {
  attendeePeople?: EventCardAttendee[];
  interval?: Interval;
  recurrence?: RecurrenceEventInfo;
  subText: string | null;
  subTextIcon?: SvgIconComponent;
}) => {
  const durationAsHoursMins = interval
    ? formatDuration(interval.length("minutes"), { unitDisplay: "narrow" })
    : null;

  const hasAttendees = attendeePeople && !isEmpty(attendeePeople);
  const showInlineDivider = subText && durationAsHoursMins;

  const isRecurring =
    recurrence &&
    recurrence.intervalType !== RecurrenceIntervalTypeEnum.NONE &&
    recurrence.intervalCount !== 0;

  const recurrenceInterval = isRecurring
    ? formatRecurrenceToSelectableOption({ ...recurrence, supportCustomRecurrence: true })
    : null;
  const recurrenceString = recurrence?.recurrenceString ?? null;

  return (
    <div className="cw-flex cw-items-center cw-caption cw-text-muted">
      {hasAttendees && <ECAttendeeStack attendees={attendeePeople} maxShown={5} />}
      {(subText || durationAsHoursMins) && (
        <div className="cw-flex cw-items-center">
          {hasAttendees && <Divider />}
          {SubTextIcon && subText && (
            <div className="cw-flex cw-items-center cw-gap-1">
              {SubTextIcon && (
                <SubTextIcon
                  className={classNames("", {
                    "cw-text-warning": SubTextIcon === Warning || SubTextIcon === LockFilled,
                  })}
                  style={{ height: 14, width: 14 }}
                />
              )}
              <div>{subText}</div>
            </div>
          )}
          {!SubTextIcon && subText && <span>{subText}</span>}
          {showInlineDivider && <span className="cw-mx-1"> ⸱ </span>}
          {durationAsHoursMins && <span>{durationAsHoursMins}</span>}
        </div>
      )}
      {recurrenceInterval && (
        <>
          {(hasAttendees || subText || durationAsHoursMins) && <Divider />}
          <Tooltip title={<div>{recurrenceString ?? `Recurs ${recurrenceInterval}`}</div>}>
            <span>{recurrenceInterval}</span>
          </Tooltip>
        </>
      )}
    </div>
  );
};

export const EventInfoWrapper = ({
  attendeePeople,
  interval,
  recurrence,
  subText,
  subTextIcon: SubTextIcon,
  isAddDiff,
  isFlexible,
}: {
  attendeePeople?: EventCardAttendee[];
  interval?: Interval;
  recurrence?: RecurrenceEventInfo;
  subText: string | null;
  subTextIcon?: SvgIconComponent;
  isAddDiff: boolean;
  isFlexible?: boolean;
}) => {
  if (isAddDiff) {
    return (
      <EventInfoNewEvent
        attendeePeople={attendeePeople}
        interval={interval}
        recurrence={recurrence}
        isFlexible={isFlexible}
      />
    );
  } else {
    return (
      <EventInfoDefault
        attendeePeople={attendeePeople}
        interval={interval}
        recurrence={recurrence}
        subText={subText}
        subTextIcon={SubTextIcon}
      />
    );
  }
};
